import { ReactNode, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TableCell from "@mui/material/TableCell";
import Tooltip from "@mui/material/Tooltip";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

/** How long the copy outcome stays on the button before it offers to copy again. */
const COPY_FEEDBACK_MS = 1500;

type CopyOutcome = "idle" | "copied" | "failed";

// The tooltip also names the button, so its accessible name follows the outcome.
const COPY_TOOLTIP: Record<CopyOutcome, string> = {
  idle: "คัดลอกเลขที่บิล",
  copied: "คัดลอกแล้ว",
  failed: "คัดลอกไม่สำเร็จ ให้เลือกเลขที่บิลแล้วคัดลอกเอง",
};

const COPY_ICON: Record<CopyOutcome, ReactNode> = {
  idle: <ContentCopyIcon fontSize="inherit" />,
  copied: <DoneIcon fontSize="inherit" color="success" />,
  failed: <ErrorOutlineIcon fontSize="inherit" color="error" />,
};

const linkSx = {
  color: "link.main",
  fontWeight: 500,
  textDecoration: "underline",
} as const;

interface BillNumberCellProps {
  transactionNo: string;
  /** The bill page, carrying the list's return ticket. */
  billPath: string;
}

/**
 * The bill number is the only part of a list row that opens the bill. It is a real link, so the
 * rest of the row can be selected and copied, and a middle-click or "open in new tab" works; the
 * button beside it copies the number without opening anything.
 */
export default function BillNumberCell({
  transactionNo,
  billPath,
}: BillNumberCellProps) {
  // Held in an object so a repeated outcome is still a new value: a second click restarts the
  // timer below instead of the first click's timer cutting the feedback short.
  const [copyFeedback, setCopyFeedback] = useState<{ outcome: CopyOutcome }>({
    outcome: "idle",
  });
  const copyOutcome = copyFeedback.outcome;

  useEffect(() => {
    if (copyFeedback.outcome === "idle") return;
    const timer = window.setTimeout(
      () => setCopyFeedback({ outcome: "idle" }),
      COPY_FEEDBACK_MS,
    );
    return () => window.clearTimeout(timer);
  }, [copyFeedback]);

  const copyBillNumber = async () => {
    try {
      await navigator.clipboard.writeText(transactionNo);
      setCopyFeedback({ outcome: "copied" });
    } catch {
      // The browser refused the clipboard (permission denied, or the page is not served over
      // HTTPS). Say so, rather than leave the user pasting whatever was copied before.
      setCopyFeedback({ outcome: "failed" });
    }
  };

  return (
    <TableCell>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Link component={RouterLink} to={billPath} sx={linkSx}>
          {transactionNo}
        </Link>
        <Tooltip title={COPY_TOOLTIP[copyOutcome]}>
          <IconButton size="small" onClick={copyBillNumber}>
            {COPY_ICON[copyOutcome]}
          </IconButton>
        </Tooltip>
      </Stack>
    </TableCell>
  );
}
